/**
 * wsr-admin Material UI theme — the single source of truth that wires the design
 * tokens (`./tokens`) onto a real MUI theme: palette, typography (incl. the
 * reusable `mono` treatment), shape, shadows, and component density.
 *
 * THEME IS INTENTIONALLY NEUTRAL: the palette/typography are carried over from
 * the dink-admin reference design system and are NOT a wsr-admin brand tier. A
 * dedicated re-theme spec can replace them later (Operator-confirmed; see the
 * WSRADM-0004 delivery). The literal color values live in `./tokens`.
 *
 * Stack: `@mui/material` + `@emotion`. Mounted once app-wide via
 * `<ThemeProvider theme={theme}><CssBaseline/>…</ThemeProvider>` in `main.tsx`.
 */
import { createTheme } from '@mui/material/styles';
import type { CSSProperties } from 'react';
import { COURT_GREEN, COURT_GREEN_DARK, MONO, PAGE_BG, SANS, SHADOWS } from './tokens';

// ---- Typography augmentation: a reusable, typed `mono` treatment ----
// Numbers, IDs and keys render in Roboto Mono. Adding it to the theme's
// typography variants makes it usable as <Typography variant="mono"> and via
// theme.typography.mono.
declare module '@mui/material/styles' {
  interface TypographyVariants {
    mono: CSSProperties;
  }
  interface TypographyVariantsOptions {
    mono?: CSSProperties;
  }
}
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    mono: true;
  }
}

export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: COURT_GREEN, dark: COURT_GREEN_DARK, contrastText: '#FFFFFF' },
    success: { main: '#2E9D5E', dark: '#1F6B43' },
    warning: { main: '#D89A2E', dark: '#8A5A12' },
    error: { main: '#C0492E', dark: '#8C2F1C' },
    background: { default: PAGE_BG, paper: '#FFFFFF' },
    text: {
      primary: 'rgba(0,0,0,0.87)',
      secondary: 'rgba(0,0,0,0.6)',
      disabled: 'rgba(0,0,0,0.38)',
    },
    divider: 'rgba(0,0,0,0.12)',
  },

  shape: { borderRadius: 8 }, // controls/inputs; cards override to 12 below.

  typography: {
    fontFamily: SANS,
    h1: { fontSize: 26, fontWeight: 500, letterSpacing: '-0.3px' },
    h2: { fontSize: 24, fontWeight: 500, letterSpacing: '-0.3px' },
    h3: { fontSize: 18, fontWeight: 500 },
    subtitle1: { fontSize: 15, fontWeight: 500 },
    body1: { fontSize: 13 },
    body2: { fontSize: 12.5, color: 'rgba(0,0,0,0.6)' },
    caption: { fontSize: 11.5 },
    button: {
      textTransform: 'uppercase',
      fontWeight: 500,
      letterSpacing: '0.4px',
      fontSize: 13,
    },
    // Reusable mono treatment (tabular feel) for numbers/IDs/keys.
    mono: { fontFamily: MONO, fontSize: 13, letterSpacing: 0 },
  },

  components: {
    // Page background.
    MuiCssBaseline: {
      styleOverrides: { body: { backgroundColor: PAGE_BG } },
    },

    // Cards / Paper: 12px radius + the layered card shadow.
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 12 },
        elevation1: { boxShadow: SHADOWS.card },
      },
    },

    MuiAppBar: {
      defaultProps: { color: 'inherit', elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          borderBottom: '1px solid rgba(0,0,0,0.12)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: { width: 248, borderRight: '1px solid rgba(0,0,0,0.12)' },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 6,
          minHeight: 38,
          paddingInline: 16,
          // v9: the removed `containedPrimary` compound-class slot moves to a
          // `variants` entry (variant: 'contained' + color: 'primary').
          variants: [
            {
              props: { variant: 'contained', color: 'primary' },
              style: { '&:hover': { backgroundColor: COURT_GREEN_DARK } },
            },
          ],
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { height: 24, borderRadius: 12, fontSize: 12, fontWeight: 500 },
        label: { paddingInline: 10 },
      },
    },

    // Dense tables by default.
    MuiTable: { defaultProps: { size: 'small' } },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: 13,
          paddingTop: 9,
          paddingBottom: 9,
          borderColor: 'rgba(0,0,0,0.06)',
        },
        head: {
          fontSize: 12,
          fontWeight: 500,
          color: 'rgba(0,0,0,0.6)',
          backgroundColor: '#FBFAF8',
          borderBottomColor: 'rgba(0,0,0,0.12)',
        },
      },
    },

    MuiTextField: { defaultProps: { variant: 'outlined', size: 'small' } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: COURT_GREEN,
          },
        },
      },
    },

    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: 12.5,
          paddingInline: 14,
          borderColor: 'rgba(0,0,0,0.18)',
          '&.Mui-selected': {
            backgroundColor: COURT_GREEN,
            color: '#fff',
            '&:hover': { backgroundColor: COURT_GREEN_DARK },
          },
        },
      },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 8, boxShadow: SHADOWS.dialog },
      },
    },

    // Tonal alerts for empty / permission / error states.
    MuiAlert: {
      styleOverrides: {
        // v9: the removed `standard{Error,Warning,Success,Info}` compound-class
        // slots move to `variants` entries (variant: 'standard' + each color).
        root: {
          borderRadius: 8,
          fontSize: 12.5,
          alignItems: 'flex-start',
          variants: [
            {
              props: { variant: 'standard', severity: 'error' },
              style: {
                backgroundColor: '#FDECEA',
                color: '#9A4A38',
                border: '1px solid #F5C6BF',
              },
            },
            {
              props: { variant: 'standard', severity: 'warning' },
              style: {
                backgroundColor: '#FFF7E8',
                color: '#7A5510',
                border: '1px solid #F2DDB0',
              },
            },
            {
              props: { variant: 'standard', severity: 'success' },
              style: {
                backgroundColor: '#E5F1E9',
                color: '#1F6B43',
              },
            },
            {
              props: { variant: 'standard', severity: 'info' },
              style: { backgroundColor: '#F0F5F1', color: '#2E5E45' },
            },
          ],
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontWeight: 500,
          fontSize: 13,
          letterSpacing: '0.4px',
          minHeight: 46,
        },
      },
    },

    MuiSkeleton: { defaultProps: { animation: 'pulse' } },
  },
});

export default theme;
